// Risk Analysis Calculation Utilities
// Implements CyberFundamentals (CyFun) methodology

import {
  AttackCategoryData,
  AttackType,
  CyFunLevel,
  ImpactLevel,
  OrganizationSize,
  ProbabilityLevel,
} from "@/types/risk-analysis";
import type { RiskLevel } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import { EntitySize } from "../types";

export function mapCyFunToRiskLevel(
  level: CyFunLevel | string | null | undefined
): RiskLevel | null {
  switch (level) {
    case "de BAZĂ":
      return "DE_BAZA";
    case "IMPORTANT":
      return "IMPORTANT";
    case "ESENȚIAL":
      return "ESENTIAL";
    default:
      return null;
  }
}

// Mapping constants for risk calculation
export const PROBABILITY_VALUES: Record<ProbabilityLevel, number> = {
  Scăzută: 0,
  Medie: 0.5,
  Ridicată: 1,
};

export const IMPACT_VALUES: Record<ImpactLevel, number> = {
  Scăzut: 0,
  Mediu: 5,
  Ridicat: 10,
};

export const ATTACK_TYPE_VALUES: Record<AttackType, number> = {
  global: 1,
  targeted: 2,
};

export const ORG_SIZE_VALUES: Record<OrganizationSize, number> = {
  S: 1,
  M: 2,
  L: 3,
};

/**
 * Calculate risk score using CyFun formula:
 * Risk Score = Probability × Impact × AttackType × OrgSize
 *
 * TODO (Robi): Replace with backend API call
 * GET /api/risk-calculation/scores?sector={id}&size={S|M|L}
 */
export function calculateRiskScore(params: {
  probability: ProbabilityLevel;
  impact: ImpactLevel;
  attackType: AttackType;
  orgSize: OrganizationSize;
}): number {
  const { probability, impact, attackType, orgSize } = params;

  const probValue = PROBABILITY_VALUES[probability];
  const impactValue = IMPACT_VALUES[impact];
  const attackTypeValue = ATTACK_TYPE_VALUES[attackType];
  const orgSizeValue = ORG_SIZE_VALUES[orgSize];

  return probValue * impactValue * attackTypeValue * orgSizeValue;
}

/**
 * Get color classes for risk score display
 * Color coding: Grey (0), Green (1-30), Yellow (31-60), Orange (61-100), Red (100+)
 */
export function getRiskScoreColor(score: number): string {
  if (score === 0) return "bg-muted text-muted-foreground";
  if (score <= 30) {
    return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
  }
  if (score <= 60) {
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
  }
  if (score <= 100) {
    return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
  }
  return "bg-destructive/10 text-destructive dark:bg-red-900/30 dark:text-red-400";
}

export function determineCyFunLevel(totalScore: number): CyFunLevel {
  if (totalScore < 100) return "de BAZĂ";
  if (totalScore < 200) return "IMPORTANT";
  return "ESENȚIAL";
}

/**
 * Calculate total scores per actor across all attack categories
 */
export function calculateActorTotals(
  attackMatrix: AttackCategoryData[]
): Record<string, number> {
  const totals: Record<string, number> = {};

  attackMatrix.forEach((category) => {
    Object.entries(category.actors).forEach(([actorId, actorData]) => {
      totals[actorId] = (totals[actorId] || 0) + actorData.score;
    });
  });

  return totals;
}

/**
 * Recalculates all derived values from an updated attack matrix
 * @param updatedMatrix - The new attack matrix after changes
 * @returns Recalculated actor totals, total score, and CyFun level
 */
export function recalculateAnalysisMetrics(
  updatedMatrix: AttackCategoryData[]
) {
  const actorTotals = calculateActorTotals(updatedMatrix);
  const totalScore = Object.values(actorTotals).reduce(
    (sum: number, val: number) => sum + val,
    0
  );
  const cyfunLevel = determineCyFunLevel(totalScore);

  return { actorTotals, totalScore, cyfunLevel };
}

/**
 * Get display label for organization size
 */
export function getOrgSizeLabel(
  size: OrganizationSize
): "MICĂ" | "MIJLOCIE" | "MARE" {
  switch (size) {
    case "S":
      return "MICĂ";
    case "M":
      return "MIJLOCIE";
    case "L":
      return "MARE";
  }
}

export function getOrgSizeLabelFromSize(size: EntitySize): OrganizationSize {
  switch (size) {
    case "MICĂ":
      return "S";
    case "MIJLOCIE":
      return "M";
    case "MARE":
      return "L";
  }
}

/**
 * Get display label for probability level
 * No translation needed - already in Romanian
 */
export function getProbabilityLabel(level: ProbabilityLevel): string {
  return level;
}

/**
 * Get CyFun level display data
 */
export function getCyFunLevelData(level: CyFunLevel): {
  label: string;
  color: string;
  variant: "default" | "secondary" | "destructive";
} {
  const data = {
    "de BAZĂ": {
      label: "de BAZĂ",
      color: "bg-green-100",
      variant: "default" as const,
    },
    IMPORTANT: {
      label: "IMPORTANT",
      color: "bg-yellow-100",
      variant: "secondary" as const,
    },
    ESENȚIAL: {
      label: "ESENȚIAL",
      color: "bg-red-100",
      variant: "destructive" as const,
    },
  };

  return data[level];
}

/**
 * Format sector names with overflow indicator
 * Displays first 3 sector names, then "+X mai mult(e)" if more exist
 *
 * @param sectorIds - Array of sector IDs to format
 * @param sectorsData - Sector data containing id and nume (name) for each sector
 * @returns Formatted string with sector names and overflow indicator
 *
 * @example
 * formatSectorNames(['I.1', 'I.2', 'I.3', 'I.4'], sectorsData)
 * // Returns: "Energie, Transport, Banking, +1 mai mult"
 */
export function formatSectorNames(
  sectorIds: string[],
  sectorsData: Array<{ id: string; nume: string }> | undefined
): string {
  if (!sectorsData || sectorIds.length === 0) {
    return `${sectorIds.length} sectoare`;
  }

  const names = sectorIds.slice(0, 3).map((sectorId) => {
    const sector = sectorsData.find((s) => s.id === sectorId);
    return sector?.nume || sectorId;
  });

  const overflow = sectorIds.length - 3;
  if (overflow > 0) {
    return `${names.join(", ")}, +${overflow} mai mult${
      overflow > 1 ? "e" : ""
    }`;
  }
  return names.join(", ");
}
